/**
 * Cross-instance panel visibility flags.
 *
 * Multiple SidePanel instances (left/right/top/bottom) listen to the same
 * window events. When one instance hides a tab, other instances must not
 * "restore" it in the same event tick. This module-level set records which
 * panel ids are currently hidden globally.
 */

const hiddenPanels = new Map<string, number>();

/** A hidden flag older than this is stale and must not block a fresh
 * user-initiated restore; it only guards the same-tick hide/restore race. */
const HIDDEN_FLAG_FRESH_MS = 250;

export const markPanelHidden = (panelId: string): void => {
    hiddenPanels.set(panelId, Date.now());
};

export const markPanelVisible = (panelId: string): void => {
    hiddenPanels.delete(panelId);
};

export const isPanelHidden = (panelId: string): boolean =>
    hiddenPanels.has(panelId);

/** True when the panel is hidden but the flag was set in an earlier event
 * cycle, so it is stale and a new restore request may proceed. */
export const isStaleHidden = (panelId: string): boolean => {
    const hiddenAt = hiddenPanels.get(panelId);
    if (hiddenAt === undefined) return false;
    return Date.now() - hiddenAt > HIDDEN_FLAG_FRESH_MS;
};

export const clearHiddenPanels = (): void => {
    hiddenPanels.clear();
};
